import Inherit from '../../../core/inherit';
import ShelfController from '../../../core/shelf.controller';
import { TraditionalStores } from '../../common';
import { User } from '../../../data';
import { can } from '../../../core/access';
import PriceListDelegate from './list.delegate';
import * as XLSX from 'xlsx';
import { isFeatureEnabled } from '../../../company/session';
import { FEATURES } from '../../../company/features';

const MODERN_STORE_TYPES = ['nacional', 'regional', 'independiente'];

class PriceListController extends Inherit(ShelfController, PriceListDelegate) {
    async initialize() {
        let priceList = null;
        let storeType = null;
        let clients = null;
        let name = null;

        if (this.isAuthenticated) {
            const id = this.props.searchParams.get('id');
            clients = await this.data.fetchClients();

            const isTraditional = TraditionalStores?.find((store) => store.value === id);

            if (!isTraditional) name = clients?.find((client) => client._id)?.name;
            else name = TraditionalStores?.find((store) => store.value === id)?.label;

            if (id) {
                priceList = await this.data.fetchPricesByType(id);
                storeType = await this.fetchStoreType(id, true);
            }
        } else this.navigateLogin();

        this.setState({
            priceList: { ...priceList, name },
            storeType,
            clients,
            loading: false,
            // Per-piso lists need both per-store price lists and store-type overrides on the API.
            canManageOverrides:
                isFeatureEnabled(FEATURES.STORE_PRICE_LISTS) &&
                isFeatureEnabled(FEATURES.STORE_OVERRIDES) &&
                can(User.getInstance()?.userType, 'store-overrides.manage')
        });
    }

    // ------- Per-piso price lists (MODERNO, admin only) --------

    async onViewModeChange(viewMode) {
        const { clientStoreType, clientPriceList } = this.state;
        if (viewMode === 'exception') {
            // Remember the client's list so we can return to it.
            this.setState((prev) => ({
                viewMode,
                clientStoreType: prev.storeType,
                clientPriceList: prev.priceList,
                selectedStoreId: null,
                hasStoreList: false,
                hasStoreSegmentation: false
            }));
            await this.loadClientStores(this.state.storeType?.value);
        } else {
            this.setState({
                viewMode,
                selectedStoreId: null,
                storeType: clientStoreType || this.state.storeType,
                priceList: clientPriceList || this.state.priceList,
                products: [],
                converted: false
            });
        }
    }

    async loadClientStores(clientId) {
        if (!clientId) return;
        try {
            const [stores, lists] = await Promise.all([
                this.data.fetchStoresByClient(clientId),
                this.data.fetchStorePriceListsByClient(clientId).catch(() => [])
            ]);
            this.setState({
                clientStores: (stores || []).filter((s) => MODERN_STORE_TYPES.includes(s.storeType)),
                storeListIds: new Set(lists.map((l) => String(l.storeId)))
            });
        } catch (err) {
            this.props.onMessage?.('error', err?.response?.data?.message || err?.message || 'Error al cargar pisos de venta');
        }
    }

    async onStoreSelect(storeId) {
        const { clientStoreType, clientPriceList } = this.state;
        this.setState({ selectedStoreId: storeId, loading: true });
        try {
            const [ownList, segOverride] = await Promise.all([
                this.data.fetchStorePriceList(storeId).catch(() => null),
                this.data.fetchStoreTypeOverride(storeId).catch(() => null)
            ]);
            // Rows follow the store's effective segmentation; prices start from its own list,
            // else the client's.
            const storeProducts = segOverride?.products?.length ? segOverride.products : clientStoreType?.products || [];
            this.setState({
                storeType: { ...clientStoreType, products: storeProducts },
                priceList: ownList || clientPriceList,
                hasStoreList: !!ownList,
                hasStoreSegmentation: !!segOverride?.products?.length,
                products: [],
                converted: false,
                loading: false
            });
        } catch (err) {
            this.props.onMessage?.('error', err?.message || 'Error al cargar el piso');
            this.setState({ loading: false });
        }
    }

    async deleteStoreList() {
        const { selectedStoreId, storeListIds, clientPriceList } = this.state;
        if (!selectedStoreId) return;
        this.setState({ deleting: true });
        try {
            const res = await this.data.deleteStorePriceList(selectedStoreId);
            if (!res?.success) throw new Error(res?.message || 'No se pudieron eliminar los precios propios');
            const next = new Set(storeListIds);
            next.delete(selectedStoreId);
            this.setState({ storeListIds: next, hasStoreList: false, priceList: clientPriceList, products: [], deleting: false });
            this.props.onMessage?.('success', 'El piso vuelve a usar los precios del cliente');
        } catch (err) {
            this.props.onMessage?.('error', err?.response?.data?.message || err?.message || 'No se pudieron eliminar los precios propios');
            this.setState({ deleting: false });
        }
    }

    async saveStoreList() {
        const { products, priceList, storeType, selectedStoreId } = this.state;
        // `products` holds the edits; untouched rows fall back to the list shown as the base.
        const edited = new Map((products?.length ? products : priceList?.products || []).map((p) => [p.productId, p]));
        const rows = (storeType?.products || []).map((id) => edited.get(id)).filter(Boolean);
        if (!rows.length) {
            this.props.onMessage?.('warning', 'La lista del piso no tiene precios');
            return;
        }
        try {
            const res = await this.data.saveStorePriceList(selectedStoreId, { products: rows });
            if (!res?._id) throw new Error(res?.message || 'No se pudo guardar la lista del piso');
            this.props.onMessage?.('success', 'Precios propios del piso guardados');
            this.navigatePrices();
        } catch (err) {
            this.props.onMessage?.('error', err?.response?.data?.message || err?.message || 'No se pudo guardar la lista del piso');
        }
    }

    async fetchStoreType(value, isInitial = false) {
        this.setState({ loading: true });

        const storeType = await this.data.fetchStoreTypeByValue(value);
        const priceList = (await this.data.fetchPricesByType(value)) || {};

        if (isInitial) return storeType;
        else this.setState({ storeType, priceList, loading: false });
    }

    handleConvert(file) {
        const storeType = this.state.storeType;
        if (file) {
            const reader = new FileReader();

            reader.onload = (e) => {
                const data = e?.target?.result;
                const workbook = XLSX.read(data, { type: 'binary' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const json = XLSX.utils.sheet_to_json(worksheet);
                let products = [];

                console.log('[Excel Upload] Raw JSON from Excel:', json);
                console.log('[Excel Upload] First row keys:', json[0] ? Object.keys(json[0]) : 'No data');
                console.log('[Excel Upload] Channel:', storeType?.channel);

                if (storeType?.channel === 'moderno') {
                    products = json.map((p) => ({
                        productId: p['Producto ID'] || p.Producto,
                        price: p.Costo,
                        suggestedPrice: p.Costo / (1 - p['Margen Sugerido'] / 100),
                    }));
                } else {
                    products = json.map((p) => ({
                        productId: p['Producto ID'] || p.Producto,
                        price: p.Costo,
                        suggestedPrice: p['Precio Sugerido'],
                    }));
                }

                console.log('[Excel Upload] Parsed products:', products);
                this.setState({ products, converted: true });
            };
            reader.readAsBinaryString(file);
        }
    }

    async createPriceList() {
        const { products, storeType, viewMode, selectedStoreId } = this.state;
        if (viewMode === 'exception') {
            if (!selectedStoreId) {
                this.props.onMessage?.('warning', 'Selecciona un piso de venta');
                return;
            }
            return this.saveStoreList();
        }

        const payload = {
            products,
            channel: storeType?.channel,
            type: storeType?.value,
        };
        const createPrice = await this.data.createPrice(payload);

        if (createPrice) this.navigatePrices();
    }
}

export default PriceListController;
